import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { comics as allComics, categories as allCategories } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug?: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'all');

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeCategory]);

  const filteredComics = activeCategory === 'all'
    ? allComics
    : allComics.filter((c) => c.categorySlug === activeCategory);

  const currentCategoryName = activeCategory === 'all'
    ? '全部漫画'
    : allCategories.find((c) => c.slug === activeCategory)?.name || '全部漫画';

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-16">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-8 md:py-10">
          <nav className="mb-6" aria-label="Breadcrumb">
            <ol className="flex items-center gap-1.5 text-sm">
              <li>
                <Link to="/" className="text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
              </li>
              <li className="text-foreground-300">
                <i className="ri-arrow-right-s-line text-xs"></i>
              </li>
              <li>
                <span className="text-foreground-700 font-medium">{currentCategoryName}</span>
              </li>
            </ol>
          </nav>

          <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
            {currentCategoryName}
          </h1>
          <p className="text-sm text-foreground-500 mb-8">
            共 <strong className="text-foreground-700">{filteredComics.length}</strong> 部作品
          </p>

          <div className="flex flex-wrap items-center gap-2 mb-8">
            <Link
              to="/category"
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                activeCategory === 'all'
                  ? 'bg-primary-500 text-white'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              全部
            </Link>
            {allCategories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  activeCategory === cat.slug
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {filteredComics.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-300">
                <i className="ri-book-open-line text-2xl"></i>
              </div>
              <p className="text-foreground-500">该分类暂无漫画作品</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
              {filteredComics.map((comic) => (
                <Link
                  key={comic.id}
                  to={`/article/${comic.slug}`}
                  className="group block cursor-pointer"
                >
                  <div className="relative overflow-hidden rounded-lg bg-background-100 aspect-[3/4]">
                    <img
                      src={comic.cover}
                      alt={`${comic.title} - ${comic.originalTitle}`}
                      title={`${comic.title} 韩国漫画`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
                      <div className="text-white text-xs">
                        <p className="font-medium">{comic.author}</p>
                        <p className="text-white/70">{comic.views.toLocaleString()} 次阅读</p>
                      </div>
                    </div>
                    {comic.status === '已完结' && (
                      <span className="absolute top-2 left-2 bg-secondary-500 text-white text-xs px-2 py-0.5 rounded-full whitespace-nowrap">
                        已完结
                      </span>
                    )}
                    {comic.status === '连载中' && (
                      <span className="absolute top-2 left-2 bg-accent-500 text-white text-xs px-2 py-0.5 rounded-full whitespace-nowrap">
                        连载中
                      </span>
                    )}
                  </div>
                  <div className="mt-2.5">
                    <h3 className="text-sm font-medium text-foreground-900 line-clamp-1 group-hover:text-primary-500 transition-colors">
                      {comic.title}
                    </h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-accent-500 font-semibold">{comic.rating}</span>
                      <span className="text-xs text-foreground-400">共{comic.chapters}话</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${currentCategoryName} - 汗汗漫画网`,
            description: `汗汗漫画网 ${currentCategoryName} 分类，收录大量好看的韩国漫画，支持韩漫在线免费阅读。`,
            url: `https://hanhanmanhua.com/category${activeCategory !== 'all' ? '/' + activeCategory : ''}`,
            isPartOf: {
              '@type': 'WebSite',
              name: '汗汗漫画网',
              url: 'https://hanhanmanhua.com/',
            },
          }),
        }}
      />
    </div>
  );
}